import { getCurrentSkuIdForProduct, getHighestProductLevelSkuIdsByProductLine, getProductForSkuId } from 'pricing-pages-lib/utils/productsUtils';
import Raven from 'raven-js';
import { CORE_SEATS_INCLUDED_STARTER } from 'self-service-api/constants/ApiNames';
const NON_ONBOARDING_API_NAMES = [CORE_SEATS_INCLUDED_STARTER];
const getOnboardingSkuIdForApiName = (state, {
  apiName
}) => {
  const onboardingSkuId = getCurrentSkuIdForProduct(state, {
    apiName
  });
  if (!onboardingSkuId) {
    const error = new Error(`Pricing pages lib: Could not find skuId for onboarding apiName: ${apiName}`);
    Raven.captureException(error);
    throw error;
  }
  return onboardingSkuId;
};
const getRequiredOnboardingObjectForSkuId = (state, {
  skuId
}) => {
  const {
    requiredOnboarding
  } = state;
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  return requiredOnboarding && apiName && apiName in requiredOnboarding ? requiredOnboarding[apiName] : undefined;
};

/**
 * Returns the list of required onboarding skuIds if the onboarding is marked
 * as required and NOT a choice the customer can make between options, i.e.
 * `hasOnboardingChoices` is marked as false while `onboardingRequired` is true
 */
export const getRequiredOnboardingSkuIds = (state, {
  skuId,
  removeNonOnboardingApiNames = true
}) => {
  const requiredOnboardingObject = getRequiredOnboardingObjectForSkuId(state, {
    skuId
  });
  if (!requiredOnboardingObject) {
    return null;
  }
  const {
    onboardingApiNames,
    onboardingRequired,
    hasOnboardingChoices
  } = requiredOnboardingObject;
  if (!onboardingRequired || hasOnboardingChoices) {
    return null;
  }
  const filteredOnboardingApiNames = removeNonOnboardingApiNames ? onboardingApiNames.filter(name => !NON_ONBOARDING_API_NAMES.includes(name)) : onboardingApiNames;
  if (!filteredOnboardingApiNames.length) {
    return null;
  }
  const onboardingSkuIds = filteredOnboardingApiNames.map(onboardingApiName => getOnboardingSkuIdForApiName(state, {
    apiName: onboardingApiName
  }));
  return onboardingSkuIds;
};

/**
 * Returns the list of onboarding skuIds the customer has to choose between
 */
export const getRequiredOnboardingChoiceSkuIds = (state, {
  skuId
}) => {
  const requiredOnboardingObject = getRequiredOnboardingObjectForSkuId(state, {
    skuId
  });
  if (!requiredOnboardingObject) {
    return null;
  }
  const {
    onboardingRequired,
    onboardingApiNames,
    hasOnboardingChoices
  } = requiredOnboardingObject;
  if (!onboardingRequired || !hasOnboardingChoices) {
    return null;
  }
  const onboardingSkuIds = onboardingApiNames.map(onboardingApiName => getOnboardingSkuIdForApiName(state, {
    apiName: onboardingApiName
  }));
  return onboardingSkuIds;
};

/**
 * Returns the recommended onboarding skuId. This should contain a value from
 * the onboardingApiNames array if these are choices the customer can choose
 * between
 */
export const getRecommendedOnboardingChoiceSkuId = (state, {
  skuId
}) => {
  const requiredOnboardingObject = getRequiredOnboardingObjectForSkuId(state, {
    skuId
  });
  if (!requiredOnboardingObject) {
    return null;
  }
  const {
    recommendedOnboardingChoice
  } = requiredOnboardingObject;
  return recommendedOnboardingChoice ? getOnboardingSkuIdForApiName(state, {
    apiName: recommendedOnboardingChoice
  }) : null;
};

/**
 * Checks `allSkuIds` and adds in:
 * - any missing required onboarding
 * - recommended onboarding if no choice is found in `allSkuIds`
 * - filters for just onboarding with or without productBasedOffers
 *
 * The filtering ensures onboarding with productBasedOffers only show up
 * in the productBasedOffers array for the purchase order generation,
 * or only shows up in checkoutProductConfigurations, never both.
 */
export const getRequiredOnboardingSkuIdsWithRecommendedFallback = (state, {
  skuId,
  allSkuIds,
  excludeFallback = false
}) => {
  const requiredOnboardingSkuIds = getRequiredOnboardingSkuIds(state, {
    skuId
  });
  const choiceOnboardingSkuIds = getRequiredOnboardingChoiceSkuIds(state, {
    skuId
  });
  let onboardingSkuIds = [];
  if (requiredOnboardingSkuIds) {
    const missingRequiredOnboardingSkuIds = requiredOnboardingSkuIds.filter(searchSkuId => !allSkuIds.includes(searchSkuId));
    onboardingSkuIds = missingRequiredOnboardingSkuIds;
  } else if (choiceOnboardingSkuIds) {
    const hasNoChoiceSelected = !choiceOnboardingSkuIds.some(searchSkuId => allSkuIds.includes(searchSkuId));
    if (hasNoChoiceSelected && !excludeFallback) {
      const recommendedChoice = getRecommendedOnboardingChoiceSkuId(state, {
        skuId
      }) || choiceOnboardingSkuIds[0];
      onboardingSkuIds = [recommendedChoice];
    }
  }
  return onboardingSkuIds;
};
export const getIsStarterCoreSeatRequiredForSkuIds = (state, {
  skuIds
}) => {
  const highestProductLevelSkuIdsByProductLine = getHighestProductLevelSkuIdsByProductLine(state, {
    skuIds
  });
  const skuIdsToUse = Object.values(highestProductLevelSkuIdsByProductLine);
  const requiredOnboardingSkuIds = skuIdsToUse.map(skuId => getRequiredOnboardingSkuIds(state, {
    skuId,
    removeNonOnboardingApiNames: false
  })).flat();
  const coreSeatsIncludedStarterSkuId = getCurrentSkuIdForProduct(state, {
    apiName: CORE_SEATS_INCLUDED_STARTER
  });
  return Boolean(requiredOnboardingSkuIds.find(skuId => skuId && skuId === coreSeatsIncludedStarterSkuId));
};

/**
 * Given a list of skuIds, returns a list of all the required onboarding skuIds
 *
 * @param state
 * @param skuIds: list of product sku ids
 * @returns list of required onboarding skuIds
 */
export const getAllRequiredOnboardingSkuIdsFromList = (state, {
  skuIds
}) => {
  const requiredOnboardingSkuIds = skuIds.flatMap(skuId => getRequiredOnboardingSkuIds(state, {
    skuId
  }));
  return requiredOnboardingSkuIds || [];
};